if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        inputMin: '.cc-price-range__input--min',
        inputMax: '.cc-price-range__input--max',
        control: '.cc-price-range__control',
        controlMin: '.cc-price-range__control--min',
        controlMax: '.cc-price-range__control--max',
        bar: '.cc-price-range__bar',
        activeBar: '.cc-price-range__bar-active'
      };

      // Controls & DOM refs
      this.controls = {
        min: {
          barControl: this.querySelector(this.selectors.controlMin),
          input: this.querySelector(this.selectors.inputMin)
        },
        max: {
          barControl: this.querySelector(this.selectors.controlMax),
          input: this.querySelector(this.selectors.inputMax)
        }
      };

      // Fallback: ensure inputs exist
      if (!this.controls.min.input || !this.controls.max.input || !this.controls.min.barControl || !this.controls.max.barControl) {
        // Missing required DOM structure — abort safely.
        return;
      }

      // Read range min/max from data attributes if provided (cents) or from input attrs (units)
      // We convert cents to units (divide by 100) so internal logic remains unit-based like before.
      var host = this; // for clarity
      function intOrNull(v) {
        if (v === undefined || v === null || v === '') return null;
        var n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      }

      // Prefer data-range-max-cents on the <price-range> host element
      var hostRangeMaxCents = intOrNull(this.dataset ? this.dataset.rangeMaxCents : null);
      var hostRangeMinCents = intOrNull(this.dataset ? this.dataset.rangeMinCents : null);

      // Inputs may also carry data attributes
      var inMaxCents = intOrNull(this.controls.max.input.dataset ? this.controls.max.input.dataset.rangeMaxCents : null);
      var inMinCents = intOrNull(this.controls.min.input.dataset ? this.controls.min.input.dataset.rangeMinCents : null);

      // Determine final cents values, preferring host -> input -> attribute fallback
      var finalRangeMaxCents = hostRangeMaxCents || inMaxCents || null;
      var finalRangeMinCents = hostRangeMinCents || inMinCents || null;

      // If cents not available, fallback to input max/min attributes (which are in units)
      if (finalRangeMaxCents === null) {
        var fallbackMaxUnits = intOrNull(this.controls.max.input.getAttribute('max')) || intOrNull(this.controls.max.input.placeholder) || 0;
        finalRangeMaxCents = Math.round((fallbackMaxUnits || 0) * 100);
      }
      if (finalRangeMinCents === null) {
        var fallbackMinUnits = intOrNull(this.controls.min.input.getAttribute('min')) || 0;
        finalRangeMinCents = Math.round((fallbackMinUnits || 0) * 100);
      }

      // Convert cents -> units for internal logic
      this.valueMin = Math.round(finalRangeMinCents / 100);
      this.valueMax = Math.round(finalRangeMaxCents / 100);

      // Read current control values (units) from inputs if present, else use placeholders
      var rawMin = this.controls.min.input.value === '' ? this.controls.min.input.placeholder : this.controls.min.input.value;
      var rawMax = this.controls.max.input.value === '' ? this.controls.max.input.placeholder : this.controls.max.input.value;

      // Parse as ints (units)
      this.controls.min.value = intOrNull(rawMin) || this.valueMin;
      this.controls.max.value = intOrNull(rawMax) || this.valueMax;

      // Ensure min/max are numeric and within range
      if (this.controls.min.value < this.valueMin) this.controls.min.value = this.valueMin;
      if (this.controls.max.value > this.valueMax) this.controls.max.value = this.valueMax;
      if (this.controls.min.value >= this.controls.max.value) {
        // Ensure at least a difference of 1
        if (this.controls.min.value > this.valueMin) {
          this.controls.min.value = Math.max(this.valueMin, this.controls.max.value - 1);
        } else {
          this.controls.max.value = Math.min(this.valueMax, this.controls.min.value + 1);
        }
      }

      this.valueRange = this.valueMax - this.valueMin;

      // Setup aria and tabindex
      [this.controls.min, this.controls.max].forEach((item) => {
        if (item.barControl) {
          item.barControl.setAttribute('aria-valuemin', this.valueMin);
          item.barControl.setAttribute('aria-valuemax', this.valueMax);
          item.barControl.setAttribute('tabindex', 0);
        }
      });
      if (this.controls.min.barControl) {
        this.controls.min.barControl.setAttribute('role', 'slider');
        this.controls.min.barControl.setAttribute('aria-valuenow', this.controls.min.value);
      }
      if (this.controls.max.barControl) {
        this.controls.max.barControl.setAttribute('role', 'slider');
        this.controls.max.barControl.setAttribute('aria-valuenow', this.controls.max.value);
      }

      this.bar = this.querySelector(this.selectors.bar);
      this.activeBar = this.querySelector(this.selectors.activeBar);
      this.inDrag = false;
      this.rtl = document.querySelector('html[dir=rtl]');

      this.bindEvents();
      this.render();
    }

    getPxToValueRatio() {
      // ensure valueMax/valueMin are numbers and avoid division by zero
      var denom = (this.valueMax - this.valueMin);
      if (!denom) return 1;
      const r = this.bar.clientWidth / denom;
      return this.rtl ? -r : r;
    }

    getPcToValueRatio() {
      var denom = (this.valueMax - this.valueMin) || 1;
      return 100.0 / denom;
    }

    setActiveControlValue(valueIn) {
      let value = valueIn;
      // only accept valid numbers
      if (Number.isNaN(parseInt(value, 10))) return;

      // clamp & default
      if (this.activeControl === this.controls.min) {
        if (value === '') {
          value = this.valueMin;
        }
        value = Math.min(value, this.controls.max.value - 1);
        value = Math.max(this.valueMin, value);
      } else {
        if (value === '') {
          value = this.valueMax;
        }
        value = Math.max(value, this.controls.min.value + 1);
        value = Math.min(this.valueMax, value);
      }

      // round
      this.activeControl.value = Math.round(value);

      // update input
      if (this.activeControl.input.value !== String(this.activeControl.value)) {
        if (String(this.activeControl.value) === String(this.activeControl.input.placeholder)) {
          this.activeControl.input.value = '';
        } else {
          this.activeControl.input.value = this.activeControl.value;
        }
        this.activeControl.input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false, detail: { sender: 'theme:component:price_range' } }));
      }

      // a11y
      if (this.activeControl.barControl) {
        this.activeControl.barControl.setAttribute('aria-valuenow', this.activeControl.value);
      }
    }

    render() {
      this.drawControl(this.controls.min);
      this.drawControl(this.controls.max);
      this.drawActiveBar();
    }

    drawControl(control) {
      const x = `${((control.value - this.valueMin) * this.getPcToValueRatio())}%`;
      if (this.rtl) {
        control.barControl.style.right = x;
      } else {
        control.barControl.style.left = x;
      }
    }

    drawActiveBar() {
      const s = `${((this.controls.min.value - this.valueMin) * this.getPcToValueRatio())}%`;
      const e = `${((this.valueMax - this.controls.max.value) * this.getPcToValueRatio())}%`;
      if (this.rtl) {
        this.activeBar.style.left = e;
        this.activeBar.style.right = s;
      } else {
        this.activeBar.style.left = s;
        this.activeBar.style.right = e;
      }
    }

    handleControlTouchStart(e) {
      e.preventDefault();
      this.startDrag(e.target, e.touches[0].clientX);
      this.boundControlTouchMoveEvent = this.handleControlTouchMove.bind(this);
      this.boundControlTouchEndEvent = this.handleControlTouchEnd.bind(this);
      window.addEventListener('touchmove', this.boundControlTouchMoveEvent, { passive: true });
      window.addEventListener('touchend', this.boundControlTouchEndEvent);
    }

    handleControlTouchMove(e) {
      this.moveDrag(e.touches[0].clientX);
    }

    handleControlTouchEnd(e) {
      e.preventDefault();
      window.removeEventListener('touchmove', this.boundControlTouchMoveEvent);
      window.removeEventListener('touchend', this.boundControlTouchEndEvent);
      this.stopDrag();
    }

    handleControlMouseDown(e) {
      e.preventDefault();
      this.startDrag(e.target, e.clientX);
      this.boundControlMouseMoveEvent = this.handleControlMouseMove.bind(this);
      this.boundControlMouseUpEvent = this.handleControlMouseUp.bind(this);
      window.addEventListener('mousemove', this.boundControlMouseMoveEvent);
      window.addEventListener('mouseup', this.boundControlMouseUpEvent);
    }

    handleControlMouseMove(e) {
      this.moveDrag(e.clientX);
    }

    handleControlMouseUp(e) {
      e.preventDefault();
      window.removeEventListener('mousemove', this.boundControlMouseMoveEvent);
      window.removeEventListener('mouseup', this.boundControlMouseUpEvent);
      this.stopDrag();
    }

    startDrag(target, startX) {
      if (this.controls.min.barControl === target) {
        this.activeControl = this.controls.min;
      } else {
        this.activeControl = this.controls.max;
      }
      this.dragStartX = startX;
      this.dragStartValue = this.activeControl.value;
      this.inDrag = true;
    }

    moveDrag(moveX) {
      if (this.inDrag) {
        const value = this.dragStartValue + (moveX - this.dragStartX) / this.getPxToValueRatio();
        this.setActiveControlValue(value);
        this.render();
      }
    }

    stopDrag() {
      this.inDrag = false;
    }

    handleControlKeyDown(evt) {
      if (evt.key === 'ArrowRight') {
        this.adjustControlFromKeypress(evt.target, 10.0);
        evt.preventDefault();
      } else if (evt.key === 'ArrowLeft') {
        this.adjustControlFromKeypress(evt.target, -10.0);
        evt.preventDefault();
      } else if (evt.key === 'Home') {
        this.adjustControlFromKeypress(evt.target, false, 'min');
        evt.preventDefault();
      } else if (evt.key === 'End') {
        this.adjustControlFromKeypress(evt.target, false, 'max');
        evt.preventDefault();
      }
    }

    adjustControlFromKeypress(control, pxAmount, minMax) {
      if (this.controls.min.barControl === control) {
        this.activeControl = this.controls.min;
      } else {
        this.activeControl = this.controls.max;
      }
      if (!minMax) {
        this.setActiveControlValue(this.activeControl.value + pxAmount / this.getPxToValueRatio());
      } else if (minMax === 'min') {
        this.setActiveControlValue(this.activeControl.input.min);
      } else {
        this.setActiveControlValue(this.activeControl.input.max);
      }
      this.render();
    }

    handleInputChange(e) {
      // strip out non numeric values
      e.target.value = e.target.value.replace(/\D/g, '');

      if (!e.detail || e.detail.sender !== 'theme:component:price_range') {
        if (this.controls.min.input === e.target) {
          this.activeControl = this.controls.min;
        } else {
          this.activeControl = this.controls.max;
        }
        this.setActiveControlValue(e.target.value);
        this.render();
      }
    }

    static handleInputKeyup(e) {
      // enforce numeric chars in the input
      setTimeout(() => {
        e.target.value = e.target.value.replace(/\D/g, '');
      }, 10);
    }

    bindEvents() {
      [this.controls.min, this.controls.max].forEach((item) => {
        item.barControl.addEventListener('touchstart', this.handleControlTouchStart.bind(this), { passive: false });
        item.barControl.addEventListener('mousedown', this.handleControlMouseDown.bind(this));
        item.barControl.addEventListener('keydown', this.handleControlKeyDown.bind(this));
        item.input.addEventListener('change', this.handleInputChange.bind(this));
        item.input.addEventListener('keyup', PriceRange.handleInputKeyup);
      });
    }
  }

  customElements.define('price-range', PriceRange);
}
